export const SIP_MONTHLY_MIN = 500;
export const SIP_MONTHLY_MAX = 100_000;
export const SIP_RATE_MIN = 1;
export const SIP_RATE_MAX = 30;
export const SIP_YEARS_MIN = 1;
export const SIP_YEARS_MAX = 30;

export interface ParsedSipField {
  value: number;
  error: string | null;
}

function rejectNegativeRaw(raw: string, current: number, label: string): ParsedSipField | null {
  if (raw.includes("-")) {
    return { value: current, error: `${label} cannot be negative.` };
  }
  return null;
}

function parseBoundedNumber(raw: string): number | null {
  const normalized = raw.trim().replace(/^0+(?=\d)/, "");
  const parsed = Number(normalized);
  if (!Number.isFinite(parsed)) {
    return null;
  }
  return parsed;
}

/**
 * Parse Sip Monthly Input.
 */
export function parseSipMonthlyInput(raw: string, current: number): ParsedSipField {
  const negative = rejectNegativeRaw(raw, current, "Monthly investment");
  if (negative) return negative;

  const parsed = parseBoundedNumber(raw);
  if (parsed === null) {
    return { value: current, error: "Enter a valid monthly investment." };
  }
  if (parsed < SIP_MONTHLY_MIN) {
    return { value: current, error: `Minimum monthly investment is ₹${SIP_MONTHLY_MIN}.` };
  }
  if (parsed > SIP_MONTHLY_MAX) {
    return {
      value: current,
      error: `Maximum monthly investment is ₹${SIP_MONTHLY_MAX.toLocaleString("en-IN")}.`,
    };
  }
  return { value: Math.round(parsed), error: null };
}

/**
 * Parse Sip Rate Input.
 */
export function parseSipRateInput(raw: string, current: number): ParsedSipField {
  const negative = rejectNegativeRaw(raw, current, "Expected return");
  if (negative) return negative;

  const parsed = parseBoundedNumber(raw);
  if (parsed === null) {
    return { value: current, error: "Enter a valid return rate." };
  }
  if (parsed < SIP_RATE_MIN || parsed > SIP_RATE_MAX) {
    return {
      value: current,
      error: `Return rate must be between ${SIP_RATE_MIN}% and ${SIP_RATE_MAX}%.`,
    };
  }
  return { value: parsed, error: null };
}

/**
 * Parse Sip Years Input.
 */
export function parseSipYearsInput(raw: string, current: number): ParsedSipField {
  const negative = rejectNegativeRaw(raw, current, "Time period");
  if (negative) return negative;

  const parsed = parseBoundedNumber(raw);
  if (parsed === null) {
    return { value: current, error: "Enter a valid time period." };
  }
  if (parsed < SIP_YEARS_MIN || parsed > SIP_YEARS_MAX) {
    return {
      value: current,
      error: `Time period must be between ${SIP_YEARS_MIN} and ${SIP_YEARS_MAX} years.`,
    };
  }
  return { value: Math.round(parsed), error: null };
}

/**
 * Are Sip Inputs Valid.
 */
export function areSipInputsValid(
  monthlyInvestment: number,
  expectedReturn: number,
  timePeriod: number
): boolean {
  return (
    monthlyInvestment >= SIP_MONTHLY_MIN &&
    monthlyInvestment <= SIP_MONTHLY_MAX &&
    expectedReturn >= SIP_RATE_MIN &&
    expectedReturn <= SIP_RATE_MAX &&
    timePeriod >= SIP_YEARS_MIN &&
    timePeriod <= SIP_YEARS_MAX
  );
}
